import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Undo2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { usePanelT } from "../../hooks/usePanelT";

const STATUS = {
    pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    suspended: "bg-red-500/15 text-red-400 border-red-500/30",
    replaced: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    cancelled: "bg-white/[0.06] text-muted-foreground border-white/10",
    rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OrderHistory({ onChanged }) {
    const { t } = usePanelT();
    const [orders, setOrders] = useState(null);

    const load = useCallback(async () => {
        const [a, tp] = await Promise.allSettled([api.get("/me/ad-accounts"), api.get("/me/ad-accounts/topups")]);
        const accounts = a.status === "fulfilled" ? a.value.data : [];
        const topups = tp.status === "fulfilled" ? tp.value.data : [];
        const rows = [
            ...accounts.filter((x) => x.wallet_debit_applied != null || x.initial_topup_usd != null).map((x) => ({
                id: x.id, kind: "account", platform: x.platform, account_type: x.account_type, name: x.account_name || x.name,
                status: x.status, paid: x.wallet_debit_applied ?? x.initial_topup_usd ?? 0, fee: x.fee_amount_applied ?? 0,
                net: x.net_to_account_applied ?? ((x.wallet_debit_applied ?? x.initial_topup_usd ?? 0) - (x.fee_amount_applied ?? 0)),
                feePct: x.fee_percent_applied, cancellable: x.cancellable, refunded: Boolean(x.refunded_at), created_at: x.created_at,
                cancelUrl: `/me/ad-accounts/${x.id}/cancel`,
            })),
            ...topups.map((x) => ({
                id: x.id, kind: "topup", platform: x.platform, account_type: x.account_type, name: x.account_name,
                status: x.status, paid: x.amount, fee: x.fee_amount, net: x.net_amount, feePct: x.fee_percent,
                cancellable: x.cancellable, refunded: Boolean(x.refunded_at), created_at: x.created_at,
                cancelUrl: `/me/ad-accounts/topups/${x.id}/cancel`,
            })),
        ].sort((p, q) => new Date(q.created_at) - new Date(p.created_at));
        setOrders(rows);
    }, []);
    useEffect(() => { load(); }, [load]);

    const cancel = async (o) => {
        if (!window.confirm(t("acc.confirmCancel", { n: Number(o.paid || 0).toFixed(2) }))) return;
        try {
            await api.post(o.cancelUrl);
            toast.success(t("acc.cancelled"));
            load();
            onChanged?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("acc.cancelFail"));
        }
    };

    return (
        <div data-testid="order-history" className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h3 className="font-display font-semibold text-lg inline-flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" /> {t("oh.title")}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{t("oh.sub")}</p>
                </div>
                <Link to="/panel/ad-accounts" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">{t("oh.all")} <ArrowUpRight className="w-3 h-3" /></Link>
            </div>
            {orders === null ? null : orders.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="order-history-empty">{t("oh.empty")}</p>
            ) : (
                <ul className="divide-y divide-white/[0.06]">
                    {orders.map((o) => (
                        <li key={o.id} data-testid={`order-${o.id}`} data-kind={o.kind} className="py-3 grid grid-cols-[1fr_auto] md:grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 md:gap-5">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider mr-1.5 px-1.5 py-0.5 rounded ${o.kind === "topup" ? "bg-primary/10 text-primary" : "bg-white/[0.06] text-muted-foreground"}`}>{o.kind === "topup" ? t("oh.topup") : t("oh.newAccount")}</span>
                                    <span className="uppercase text-[10px] font-mono tracking-wider text-muted-foreground mr-1.5">{o.platform}{o.account_type ? ` · ${o.account_type}` : ""}</span>
                                    {o.name}
                                </p>
                                <p className="text-[11px] font-mono text-muted-foreground mt-0.5">{new Date(o.created_at).toLocaleString()} · {o.feePct != null ? `${Number(o.feePct).toFixed(2)}%` : ""}</p>
                            </div>
                            <span data-testid={`order-status-${o.id}`} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border justify-self-end md:justify-self-auto ${STATUS[o.status] || ""}`}>{t(`st.${o.status}`)}</span>
                            <div className="text-right hidden md:block">
                                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("oh.paid")}</p>
                                <p className="font-display font-semibold text-sm tabular-nums">{money(o.paid)}</p>
                            </div>
                            <div className="text-right hidden md:block">
                                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fee")} → {t("toAdAccount")}</p>
                                <p className="font-display font-semibold text-sm tabular-nums"><span className="text-red-300">−{money(o.fee)}</span> → <span className="text-emerald-300">{money(o.net)}</span></p>
                            </div>
                            <div className="col-span-2 md:col-span-1 md:justify-self-end">
                                {o.cancellable ? (
                                    <Button size="sm" variant="outline" onClick={() => cancel(o)} data-testid={`order-cancel-${o.id}`} className="h-8 border-red-500/30 text-red-300 bg-red-500/[0.05] hover:bg-red-500/[0.12] text-xs w-full md:w-auto"><Undo2 className="w-3 h-3 mr-1" /> {t("acc.cancelRefund")}</Button>
                                ) : o.refunded ? (
                                    <span className="text-[11px] font-mono text-emerald-400" data-testid={`order-refunded-${o.id}`}>{t("oh.refunded")} {money(o.paid)}</span>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
